import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { templateAPI, categoryAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Edit, Trash2, Play, Image, Video, Globe, Lock, Crown, Eye, Copy, FileText, Calendar } from 'lucide-react'
import { OccasionIndicator } from '../OccasionScheduler'
import OccasionScheduler from '../OccasionScheduler/OccasionScheduler'
import { formatOccasionDisplay, isOccasionActive } from '../../utils/occasionUtils'
import CloneTemplateDialog from '../../editor/components/CloneTemplateDialog'
import EnhancedTemplateCard from '../TemplateCard/EnhancedTemplateCard'
import MessageDialog from '../Common/MessageDialog'

const TemplateGrid = ({ category = 'all', enableBulkActions = false, viewMode = 'grid', searchQuery = '', filters = {} }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [hoveredTemplate, setHoveredTemplate] = useState(null)
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTemplates, setSelectedTemplates] = useState(new Set())
  const [bulkActionLoading, setBulkActionLoading] = useState(false)
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [showCloneDialog, setShowCloneDialog] = useState(false)
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false)
  const [bulkTagInput, setBulkTagInput] = useState('')
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false)
  const [showBulkSchedulingDialog, setShowBulkSchedulingDialog] = useState(false)
  const [bulkSchedulingConfig, setBulkSchedulingConfig] = useState({})
  const [showBulkNameChangeDialog, setShowBulkNameChangeDialog] = useState(false)
  const [bulkNameInput, setBulkNameInput] = useState('')
  const [bulkDescriptionInput, setBulkDescriptionInput] = useState('')
  const occasionSchedulerRef = useRef(null)
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [selectedBulkCategory, setSelectedBulkCategory] = useState('')
  const [selectedBulkSubcategory, setSelectedBulkSubcategory] = useState('')
  const [templateToClone, setTemplateToClone] = useState(null)
  const [messageDialog, setMessageDialog] = useState({ isOpen: false, type: 'info', title: '', message: '' })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  })
  const [loadingMore, setLoadingMore] = useState(false)
  const [pauseAutoRefresh, setPauseAutoRefresh] = useState(false)

  const fetchTemplates = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      setError(null)

      const params = {
        page,
        limit: pagination.limit
      }

      if (category && category !== 'all') {
        params.category_id = category
      }

      // Add search query if provided
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim()
      }

      // Add advanced filters
      if (filters && typeof filters === 'object') {
        if (filters.type) params.type = filters.type
        if (filters.is_public !== undefined && filters.is_public !== null) params.is_public = filters.is_public
        if (filters.is_premium !== undefined && filters.is_premium !== null) params.is_premium = filters.is_premium
        if (filters.stage) params.stage = filters.stage
        if (filters.is_occasion_applicable !== undefined && filters.is_occasion_applicable !== null) params.is_occasion_applicable = filters.is_occasion_applicable
        if (filters.occasion_start_date) params.occasion_start_date = filters.occasion_start_date
        if (filters.has_sample !== undefined && filters.has_sample !== null) params.has_sample = filters.has_sample
        if (filters.category_id) params.category_id = filters.category_id
        // Sorting: order_by (created_at | updated_at), always descending
        if (filters.order_by) params.order_by = filters.order_by
        if (filters.order_desc !== undefined && filters.order_desc !== null) params.order_desc = filters.order_desc
      }

      const response = await templateAPI.getAll(params)

      if (response.data.success) {
        // Support both shapes:
        // A) { data: [ ... ], pagination: { page, limit, total, pages } }
        // B) { data: { templates: [ ... ], page, limit, total, totalPages, pagination: { currentPage, itemsPerPage, totalItems, totalPages } } }
        const payload = response.data.data
        let newTemplates = []
        let pg = response.data.pagination || {}

        if (Array.isArray(payload)) {
          newTemplates = payload
        } else if (payload && Array.isArray(payload.templates)) {
          newTemplates = payload.templates
          const dp = payload.pagination || {}
          pg = {
            page: dp.currentPage || payload.page || pg.page || page,
            limit: dp.itemsPerPage || payload.limit || pg.limit || pagination.limit,
            total: dp.totalItems || payload.total || pg.total || 0,
            pages: dp.totalPages || payload.totalPages || pg.pages || Math.ceil(((dp.totalItems || payload.total || 0) / (dp.itemsPerPage || payload.limit || pagination.limit)) || 0)
          }
        } else {
          newTemplates = payload || []
        }

        if (append) {
          setTemplates(prev => [...prev, ...newTemplates])
        } else {
          setTemplates(newTemplates)
        }

        setPagination({
          page: (pg.page ?? page),
          limit: (pg.limit ?? pagination.limit),
          total: (pg.total ?? 0),
          totalPages: (pg.pages ?? pg.totalPages ?? 0)
        })
      } else {
        setError(response.data.message || 'Failed to fetch templates')
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
      setError('Failed to load templates')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, pagination.limit, searchQuery, JSON.stringify(filters)])

  // Fetch templates from API when category, search query, or filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTemplates(1, false)
  }, [category, searchQuery, JSON.stringify(filters)])

  // Fetch categories for bulk category change - only root categories (parent categories)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Fetch only root categories (categories without parent) for template type
        const response = await categoryAPI.getAll({
          type: 'template',
          parentId: '' // Empty string means no parent (root categories)
        })
        if (response.data.success) {
          // Handle different possible response structures
          const categoriesData = response.data.data?.categories || response.data.data || []

          // Ensure we have an array and filter out invalid entries
          // Also filter to only include root categories (no parentId)
          const validCategories = Array.isArray(categoriesData)
            ? categoriesData.filter(cat =>
              cat &&
              typeof cat === 'object' &&
              (cat.id || cat._id) &&
              (!cat.parentId && !cat.parent_id) // Only root categories
            )
            : []

          // Sort categories by name
          validCategories.sort((a, b) => {
            const nameA = String(a.name || '').toLowerCase()
            const nameB = String(b.name || '').toLowerCase()
            return nameA.localeCompare(nameB)
          })

          console.log('Fetched root categories:', validCategories)
          setCategories(validCategories)
        } else {
          console.error('Failed to fetch categories:', response.data.message)
          setCategories([])
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
        setCategories([])
      }
    }

    if (enableBulkActions) {
      fetchCategories()
    }
  }, [enableBulkActions])

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!selectedBulkCategory) {
        setSubcategories([])
        return
      }

      try {
        // Use getSubCategories with type parameter for template categories
        const response = await categoryAPI.getAll({
          type: 'template',
          parent_id: selectedBulkCategory
        })
        if (response.data.success) {
          // Handle different possible response structures
          const subcategoriesData = response.data.data?.categories || response.data.data?.subcategories || response.data.data || []

          // Ensure we have an array and filter out invalid entries
          const validSubcategories = Array.isArray(subcategoriesData)
            ? subcategoriesData.filter(subcat => subcat && typeof subcat === 'object' && (subcat.id || subcat._id))
            : []

          // Sort subcategories by name
          validSubcategories.sort((a, b) => {
            const nameA = String(a.name || '').toLowerCase()
            const nameB = String(b.name || '').toLowerCase()
            return nameA.localeCompare(nameB)
          })

          console.log('Fetched subcategories:', validSubcategories)
          setSubcategories(validSubcategories)
        } else {
          console.error('Failed to fetch subcategories:', response.data.message)
          setSubcategories([])
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error)
        setSubcategories([])
      }
    }

    fetchSubcategories()
  }, [selectedBulkCategory])

  // Jump directly to a specific page (replaces the current list rather than appending)
  const goToPage = (page) => {
    if (
      page < 1 ||
      page > pagination.totalPages ||
      page === pagination.page ||
      loading ||
      loadingMore
    ) {
      return
    }
    fetchTemplates(page, false)
    // Scroll back to the top of the list so the new page is visible from the start
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Build a compact page number list with ellipsis, e.g. [1, '...', 4, 5, 6, '...', 42]
  const getPageNumbers = (current, total) => {
    const pages = []
    const delta = 2 // pages shown on each side of the current page
    const range = []
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.push(i)
    }
    if (current - delta > 2) {
      range.unshift('...')
    }
    if (current + delta < total - 1) {
      range.push('...')
    }
    range.unshift(1)
    if (total > 1) {
      range.push(total)
    }
    // De-duplicate (guards small totals) while preserving order
    let last
    for (const p of range) {
      if (p !== last) pages.push(p)
      last = p
    }
    return pages
  }

  // No fallback templates - show empty state instead

  // eslint-disable-next-line no-unused-vars
  const animationTemplates = [
    {
      id: 4,
      title: 'Photo and Video Animations',
      category: 'Animation',
      thumbnail: '/api/placeholder/300/200',
      gradient: 'from-pink-400 to-purple-500',
      isPro: true,
      isVideo: true
    },
    {
      id: 5,
      title: 'Subscribe Animation',
      category: 'Social Media',
      thumbnail: '/api/placeholder/300/200',
      gradient: 'from-gray-900 to-black',
      isPro: true,
      isVideo: true
    }
  ]



  const handleTemplateClick = (template) => {
    navigate(`/editor/${template?.id}`)
  }

  const handleEditTemplate = (e, template) => {
    e.stopPropagation() // Prevent template click
    navigate(`/templates/edit/${template?.id}`)
  }

  const handleDeleteTemplate = async (e, template) => {
    // Handle both signatures: (e, template) and (template_id)
    let template_id, templateObj
    if (typeof e === 'string') {
      // Called with just template_id
      template_id = e
      templateObj = templates.find(t => t.id === template_id)
    } else {
      // Called with (e, template)
      e.stopPropagation() // Prevent template click
      template_id = template?.id
      templateObj = template
    }

    if (!templateObj) return

    if (window.confirm(`Are you sure you want to delete "${templateObj.name || templateObj.title}"? This action cannot be undone.`)) {
      try {
        setActionLoading && setActionLoading(prev => ({ ...prev, [template_id]: 'deleting' }))

        const response = await templateAPI.delete(template_id)

        if (response.data.success) {
          // Remove the template from the local state
          setTemplates(prev => prev.filter(t => t.id !== template_id))
          setDeleteConfirm && setDeleteConfirm(null)
          const successMessage = response.data.message || 'Template deleted successfully!'
          setMessageDialog({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: successMessage
          })
        } else {
          const errorMessage = response.data.message || 'Unknown error'
          setMessageDialog({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: `Failed to delete template: ${errorMessage}`
          })
        }
      } catch (error) {
        console.error('Error deleting template:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete template. Please try again.'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: errorMessage
        })
      } finally {
        setActionLoading && setActionLoading(prev => ({ ...prev, [template_id]: null }))
      }
    }
  }

  const handleCloneTemplate = (template) => {
    setTemplateToClone(template)
    setShowCloneDialog(true)
  }

  const handleConfirmClone = async (newName) => {
    if (!templateToClone) return

    try {
      setActionLoading(prev => ({ ...prev, [templateToClone.id]: 'cloning' }))

      const response = await templateAPI.clone(templateToClone.id, { name: newName })

      if (response.data.success) {
        // Add cloned template to the beginning of the list
        setTemplates(prev => [response.data.data, ...prev])
        setShowCloneDialog(false)
        setTemplateToClone(null)
        const successMessage = response.data.message || 'Template cloned successfully!'
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Failed to clone template'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: errorMessage
        })
      }
    } catch (error) {
      console.error('Error cloning template:', error)
      const errorMessage = error.response?.data?.message || 'Failed to clone template'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [templateToClone.id]: null }))
    }
  }

  const handleCancelClone = () => {
    setShowCloneDialog(false)
    setTemplateToClone(null)
  }

  // Bulk action functions
  const toggleTemplateSelection = (template_id) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev)
      if (newSet.has(template_id)) {
        newSet.delete(template_id)
      } else {
        newSet.add(template_id)
      }
      return newSet
    })
  }

  const selectAllTemplates = () => {
    setSelectedTemplates(new Set(templates.map(t => t.id)))
  }

  const clearSelection = () => {
    setSelectedTemplates(new Set())
  }

  const handleBulkUpdate = async (updates) => {
    if (selectedTemplates.size === 0) return

    setBulkActionLoading(true)
    try {
      const response = await templateAPI.bulkUpdate({
        template_ids: Array.from(selectedTemplates),
        updates
      })

      if (response.data.success) {
        // Refresh templates
        if (!pauseAutoRefresh) {
          await fetchTemplates(1, false)
          clearSelection()
        }
        const successMessage = response.data.message || `Successfully updated ${response.data.data.count} templates`
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Unknown error'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: `Failed to update templates: ${errorMessage}`
        })
      }
    } catch (error) {
      console.error('Error updating templates:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update templates. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkGenerateSamples = async () => {
    if (selectedTemplates.size === 0) return

    setBulkActionLoading(true)
    try {
      const response = await templateAPI.bulkGenerateSamples({
        template_ids: Array.from(selectedTemplates)
      })

      if (response.data.success) {
        // Queue-based generation - show processing message
        const successMessage = response.data.message || `Sample generation started for ${selectedTemplates.size} templates! Processing in background...`
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Sample Generation Started',
          message: successMessage
        })

        // Refresh templates after a delay to show updated status
        if (!pauseAutoRefresh) {
          setTimeout(() => {
            fetchTemplates(1, false)
          }, 2000)
          clearSelection()
        }
      } else {
        const errorMessage = response.data.message || 'Unknown error'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: `Failed to generate samples: ${errorMessage}`
        })
      }
    } catch (error) {
      console.error('Error generating samples:', error)
      const errorMessage = error.response?.data?.message || 'Failed to generate samples. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkApprove = async () => {
    if (selectedTemplates.size === 0) return

    const message = prompt('Enter approval message (optional):')
    if (message === null) return // User cancelled

    setBulkActionLoading(true)
    try {
      const promises = Array.from(selectedTemplates).map(template_id =>
        templateAPI.updateStage(template_id, 'approved', message || '')
      )

      await Promise.all(promises)

      // Refresh templates
      if (!pauseAutoRefresh) {
        await fetchTemplates(1, false)
        clearSelection()
      }
      const successMessage = `Successfully approved ${selectedTemplates.size} template(s)`
      setMessageDialog({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: successMessage
      })
    } catch (error) {
      console.error('Error approving templates:', error)
      const errorMessage = error.response?.data?.message || 'Failed to approve templates. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkReject = async () => {
    if (selectedTemplates.size === 0) return

    const message = prompt('Enter rejection reason (required):')
    if (!message || message.trim() === '') {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Rejection reason is required'
      })
      return
    }

    setBulkActionLoading(true)
    try {
      const promises = Array.from(selectedTemplates).map(template_id =>
        templateAPI.updateStage(template_id, 'rejected', message)
      )

      await Promise.all(promises)

      // Refresh templates
      if (!pauseAutoRefresh) {
        await fetchTemplates(1, false)
        clearSelection()
      }
      const successMessage = `Successfully rejected ${selectedTemplates.size} template(s)`
      setMessageDialog({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: successMessage
      })
    } catch (error) {
      console.error('Error rejecting templates:', error)
      const errorMessage = error.response?.data?.message || 'Failed to reject templates. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedTemplates.size === 0) return

    const confirmMessage = `Are you sure you want to delete ${selectedTemplates.size} template(s)? This action will soft delete the templates and cannot be undone.`
    if (!window.confirm(confirmMessage)) {
      return
    }

    setBulkActionLoading(true)
    try {
      const promises = Array.from(selectedTemplates).map(template_id =>
        templateAPI.delete(template_id)
      )

      await Promise.all(promises)

      // Refresh templates
      if (!pauseAutoRefresh) {
        await fetchTemplates(1, false)
        clearSelection()
      }
      const successMessage = `Successfully deleted ${selectedTemplates.size} template(s)`
      setMessageDialog({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: successMessage
      })
    } catch (error) {
      console.error('Error deleting templates:', error)
      const errorMessage = error.response?.data?.message || 'Failed to delete templates. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkAddTags = async () => {
    if (selectedTemplates.size === 0) return
    if (!bulkTagInput.trim()) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Please enter tags to add'
      })
      return
    }

    const tagsToAdd = bulkTagInput.split(',').map(tag => tag.trim()).filter(tag => tag)
    if (tagsToAdd.length === 0) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Please enter valid tags'
      })
      return
    }

    setBulkActionLoading(true)
    try {
      const response = await templateAPI.bulkAddTags({
        template_ids: Array.from(selectedTemplates),
        tags: tagsToAdd
      })

      if (response.data.success) {
        // Refresh templates
        if (!pauseAutoRefresh) {
          await fetchTemplates(1, false)
          clearSelection()
        }
        setShowBulkTagDialog(false)
        setBulkTagInput('')
        const successMessage = response.data.message || `Successfully added tags to ${response.data.data.count} template(s)`
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Unknown error'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: `Failed to add tags: ${errorMessage}`
        })
      }
    } catch (error) {
      console.error('Error adding tags:', error)
      const errorMessage = error.response?.data?.message || 'Failed to add tags. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkNameChange = async () => {
    if (selectedTemplates.size === 0) return
    if (!bulkNameInput.trim() && !bulkDescriptionInput.trim()) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Please enter a name or description to update'
      })
      return
    }

    setBulkActionLoading(true)
    try {
      const templateIds = Array.from(selectedTemplates)
      const baseName = bulkNameInput.trim()
      const description = bulkDescriptionInput.trim()

      // If name is provided and multiple templates are selected, update each with a counter
      if (baseName && templateIds.length > 1) {
        let successCount = 0
        let failCount = 0
        for (let i = 0; i < templateIds.length; i++) {
          const updates = { name: `${baseName} ${i + 1}` }
          if (description) updates.description = description
          try {
            const resp = await templateAPI.update(templateIds[i], updates)
            if (resp.data.success || resp.data.status === 'success') {
              successCount++
            } else {
              failCount++
            }
          } catch (err) {
            failCount++
          }
        }
        if (!pauseAutoRefresh) {
          await fetchTemplates(1, false)
          clearSelection()
        }
        setShowBulkNameChangeDialog(false)
        setBulkNameInput('')
        setBulkDescriptionInput('')
        setMessageDialog({
          isOpen: true,
          type: failCount === 0 ? 'success' : 'warning',
          title: failCount === 0 ? 'Success' : 'Partial Success',
          message: `Updated ${successCount} template(s) with sequential names.${failCount > 0 ? ` ${failCount} failed.` : ''}`
        })
      } else {
        // Single template or only description — use bulk update
        const updates = {}
        if (baseName) updates.name = baseName
        if (description) updates.description = description

        const response = await templateAPI.bulkUpdate({
          template_ids: templateIds,
          updates
        })

        if (response.data.success) {
          if (!pauseAutoRefresh) {
            await fetchTemplates(1, false)
            clearSelection()
          }
          setShowBulkNameChangeDialog(false)
          setBulkNameInput('')
          setBulkDescriptionInput('')
          const successMessage = response.data.message || `Successfully updated name/description for ${response.data.data?.count || selectedTemplates.size} template(s)`
          setMessageDialog({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: successMessage
          })
        } else {
          const errorMessage = response.data.message || 'Unknown error'
          setMessageDialog({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: `Failed to update name/description: ${errorMessage}`
          })
        }
      }
    } catch (error) {
      console.error('Error updating name/description:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update name/description. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkCategoryChange = async () => {
    if (selectedTemplates.size === 0) return
    if (!selectedBulkCategory) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Please select a category'
      })
      return
    }

    setBulkActionLoading(true)
    try {
      const updates = {
        category_id: selectedBulkCategory
      }

      // Add subcategory if selected
      if (selectedBulkSubcategory) {
        updates.sub_category_id = selectedBulkSubcategory
      }

      const response = await templateAPI.bulkUpdate({
        template_ids: Array.from(selectedTemplates),
        updates
      })

      if (response.data.success) {
        // Refresh templates
        if (!pauseAutoRefresh) {
          await fetchTemplates(1, false)
          clearSelection()
        }
        setShowBulkCategoryDialog(false)
        setSelectedBulkCategory('')
        setSelectedBulkSubcategory('')
        const successMessage = response.data.message || `Successfully updated category for ${response.data.data.count} templates`
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Unknown error'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: `Failed to update categories: ${errorMessage}`
        })
      }
    } catch (error) {
      console.error('Error updating categories:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update categories. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkSchedulingUpdate = async () => {
    if (selectedTemplates.size === 0) return

    // Get the config from the OccasionScheduler ref
    const config = occasionSchedulerRef.current?.getConfig()
    if (!config) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Warning',
        message: 'Please configure the scheduling options'
      })
      return
    }

    // Validate the config
    const isValid = occasionSchedulerRef.current?.validate()
    if (!isValid) {
      setMessageDialog({
        isOpen: true,
        type: 'warning',
        title: 'Validation Error',
        message: 'Please fix the scheduling configuration errors before applying'
      })
      return
    }

    setBulkActionLoading(true)
    try {
      const updates = {
        isOccasionDateApplicable: config.isOccasionDateApplicable,
        occasionType: config.occasionType,
        occasionStartDate: config.occasionStartDate || null,
        occasionEndDate: config.occasionEndDate || null,
        occasionStartTime: config.occasionStartTime || null,
        occasionEndTime: config.occasionEndTime || null,
        isRecurringYearly: config.isRecurringYearly || false,
        weeklyRecurrenceDays: config.weeklyRecurrenceDays || [],
        monthlyRecurrenceDays: config.monthlyRecurrenceDays || [],
        occasionTimezone: config.occasionTimezone || 'Asia/Kolkata',
        occasionMetadata: config.occasionMetadata || null
      }

      const response = await templateAPI.bulkUpdate({
        template_ids: Array.from(selectedTemplates),
        updates
      })

      if (response.data.success) {
        // Refresh templates
        if (!pauseAutoRefresh) {
          await fetchTemplates(1, false)
          clearSelection()
        }
        setShowBulkSchedulingDialog(false)
        setBulkSchedulingConfig({})
        const successMessage = response.data.message || `Successfully updated scheduling for ${response.data.data.count} templates`
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Unknown error'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: `Failed to update scheduling: ${errorMessage}`
        })
      }
    } catch (error) {
      console.error('Error updating scheduling:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update scheduling. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setBulkActionLoading(false)
    }
  }

  // Template card handlers
  const handleTemplateEdit = (template) => {
    navigate(`/templates/edit/${template?.id}`)
  }

  const handleTemplateDelete = (template) => {
    setDeleteConfirm(template?.id)
  }

  const handleTemplateUse = (template) => {
    navigate(`/editor/${template?.id}`)
  }

  const handleGenerateSample = async (template) => {
    if (!template?.id) return

    try {
      setActionLoading(prev => ({ ...prev, [template.id]: 'generating' }))
      const response = await templateAPI.generateSample(template.id)

      if (response.data.success) {
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: response.data.message || 'Sample generation started successfully! Processing in background...'
        })

        // Refresh templates after a delay to show updated status
        if (!pauseAutoRefresh) {
          setTimeout(() => {
            fetchTemplates(1, false)
          }, 2000)
        }
      } else {
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: response.data.message || 'Failed to generate sample'
        })
      }
    } catch (error) {
      console.error('Error generating sample:', error)
      const errorMessage = error.response?.data?.message || 'Failed to generate sample. Please try again.'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [template.id]: null }))
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Loading templates...</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <div className="aspect-[3/4] bg-gray-200 animate-pulse"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load templates</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={fetchTemplates}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Use only API data - no fallback templates
  const displayTemplates = templates

  return (
    <div className="space-y-8">
      {/* Templates Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {category === 'all' ? 'All Templates' : `All Templates filtered by category id - ${category}`}
          </h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {displayTemplates.length} template{displayTemplates.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => {
                setShowBulkActions(!showBulkActions)
                if (showBulkActions) {
                  clearSelection()
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showBulkActions
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {showBulkActions ? 'Cancel Selection' : 'Bulk Actions'}
            </button>
          </div>
        </div>

        {/* Top Pagination - quick navigation without scrolling to the bottom */}
        {pagination.totalPages > 1 && (
          <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
              {pagination.total ? ` · ${pagination.total} templates` : ''}
            </span>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={goToPage}
              pageNumbers={getPageNumbers(pagination.page, pagination.totalPages)}
              disabled={loading || loadingMore}
            />
          </div>
        )}

        {/* Bulk Actions Toolbar - sticky so action buttons stay reachable while scrolling */}
        {showBulkActions && (
          <div className="sticky top-0 z-30 mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-md backdrop-blur-sm">
            {/* Top Row: Selection info and controls */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-blue-200">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-sm font-semibold text-blue-900">
                    {selectedTemplates.size} selected
                  </span>
                </div>
                <button
                  onClick={selectAllTemplates}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2.5 py-1.5 rounded-md transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-md transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={() => setPauseAutoRefresh(prev => !prev)}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${pauseAutoRefresh
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }`}
                  title={pauseAutoRefresh ? 'Auto-refresh is paused. Click to resume.' : 'Pause auto-refresh to chain multiple actions'}
                >
                  {pauseAutoRefresh ? '⏸ Paused' : '⏸ Pause'}
                </button>
                {pauseAutoRefresh && (
                  <button
                    onClick={() => {
                      setPauseAutoRefresh(false)
                      fetchTemplates(1, false)
                      clearSelection()
                    }}
                    className="text-xs font-medium px-2.5 py-1.5 rounded-md bg-green-100 text-green-800 border border-green-300 hover:bg-green-200 transition-colors"
                  >
                    ▶ Refresh
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons - Two Rows */}
            {selectedTemplates.size > 0 && (
              <div className="space-y-2">
                {/* Row 1: Visibility, Premium, Stage actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleBulkUpdate({ is_public: true })}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>
                    Public
                  </button>
                  <button
                    onClick={() => handleBulkUpdate({ is_public: false })}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    Private
                  </button>
                  <button
                    onClick={() => handleBulkUpdate({ isPremium: true })}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    Premium
                  </button>
                  <button
                    onClick={() => handleBulkUpdate({ isPremium: false })}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-500 text-white text-xs font-medium rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Free
                  </button>

                  <div className="w-px h-6 bg-blue-200 mx-1"></div>

                  <button
                    onClick={handleBulkApprove}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    Approve
                  </button>
                  <button
                    onClick={handleBulkReject}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    Reject
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-700 text-white text-xs font-medium rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Delete
                  </button>
                </div>

                {/* Row 2: Generate, Tags, Category, Scheduling, Name Change */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleBulkGenerateSamples}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Generate Samples
                  </button>
                  <button
                    onClick={() => setShowBulkTagDialog(true)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-500 text-white text-xs font-medium rounded-lg hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                    Add Tags
                  </button>
                  <button
                    onClick={() => setShowBulkCategoryDialog(true)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 text-white text-xs font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                    Change Category
                  </button>
                  <button
                    onClick={() => setShowBulkSchedulingDialog(true)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 text-white text-xs font-medium rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Scheduling
                  </button>
                  <button
                    onClick={() => setShowBulkNameChangeDialog(true)}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    Name Change
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {displayTemplates.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {displayTemplates.map((template) => (
                <EnhancedTemplateCard
                  key={template?.id}
                  template={{
                    ...template,
                    title: template?.name || template?.title,
                    thumbnail: template?.thumbnailUrl || template?.thumbnail,
                    category: template?.Category?.name || template?.category?.name || template?.category || 'General',
                    SubCategory: template?.SubCategory || template?.Subcategory || template?.sub_category,
                    Subcategory: template?.SubCategory || template?.Subcategory || template?.sub_category,
                    subcategory: template?.SubCategory || template?.Subcategory || template?.subcategory || template?.sub_category,
                    isPro: template?.isPremium || template?.isPro || false
                  }}
                  showActions={true}
                  onEdit={handleTemplateEdit}
                  onDelete={handleTemplateDelete}
                  onClone={handleCloneTemplate}
                  onUse={handleTemplateUse}
                  onGenerateSample={handleGenerateSample}
                  actionLoading={actionLoading}
                  isSelected={selectedTemplates.has(template?.id)}
                  onSelect={toggleTemplateSelection}
                  showBulkActions={showBulkActions}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table Headers */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium text-gray-500 border-b border-gray-200">
                <div className="col-span-4">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Category / Subcategory</div>
                <div className="col-span-1">Stage</div>
                <div className="col-span-1"></div>
              </div>

              {/* Template Rows */}
              {displayTemplates.map((template) => (
                <div
                  key={template?.id}
                  className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="col-span-4 flex items-center space-x-3">
                    <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                      {template?.thumbnailUrl ? (
                        <img
                          src={template?.thumbnailUrl}
                          alt={template?.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-gray-900 truncate">{template?.name}</span>
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${template?.stage === 'approved' ? 'bg-green-100 text-green-800' :
                        template?.stage === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                          template?.stage === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                      }`}>
                      {template?.stage || 'pending'}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-600">
                    {template?.is_public !== false ? 'Public' : 'Private'}
                  </div>
                  <div className="col-span-2 flex flex-col text-sm text-gray-600">
                    <span className="font-medium">{template?.Category?.name || template?.category?.name || template?.category || 'General'}</span>
                    {(template?.SubCategory?.name || template?.Subcategory?.name || template?.sub_category?.name || template?.subcategory?.name) ? (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {template?.SubCategory?.name || template?.Subcategory?.name || template?.sub_category?.name || template?.subcategory?.name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 mt-0.5 italic">none</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center text-sm">
                    {template?.type === 'video' ? 'Video' : 'Image'}
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleTemplateUse(template)}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Use Template
                    </button>
                    <button
                      onClick={() => handleGenerateSample(template)}
                      disabled={actionLoading[template?.id] === 'generating'}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Generate sample"
                    >
                      {actionLoading[template?.id] === 'generating' ? (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Generating...</span>
                        </div>
                      ) : (
                        'Generate Sample'
                      )}
                    </button>
                    <button className="p-1 hover:bg-gray-200 rounded">
                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-500 mb-6">
                {category === 'all'
                  ? 'There are no templates available yet. Create your first template to get started.'
                  : `No templates found in the ${category} category. Try selecting a different category or create a new template?.`
                }
              </p>
              <button
                onClick={() => navigate('/templates/create')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Template
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-3">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={goToPage}
            pageNumbers={getPageNumbers(pagination.page, pagination.totalPages)}
            disabled={loading || loadingMore}
          />
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
            {pagination.total ? ` · ${pagination.total} templates total` : ''}
          </span>
        </div>
      )}

      {/* Refresh Button - show when using fallback data */}
      {templates.length === 0 && displayTemplates.length > 0 && (
        <div className="text-center">
          <button
            onClick={() => fetchTemplates(1, false)}
            className="px-8 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Refresh Templates
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Template
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteTemplate(deleteConfirm)}
                disabled={actionLoading[deleteConfirm] === 'deleting'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading[deleteConfirm] === 'deleting' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Dialog */}
      <CloneTemplateDialog
        isOpen={showCloneDialog}
        onClose={handleCancelClone}
        onConfirm={handleConfirmClone}
        originalTemplateName={templateToClone?.name || ''}
        isCloning={templateToClone ? actionLoading[templateToClone.id] === 'cloning' : false}
      />

      {/* Bulk Add Tags Dialog */}
      {showBulkTagDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Tags to Selected Templates</h3>
            <p className="text-gray-600 mb-4">
              Enter tags separated by commas. These tags will be added to all {selectedTemplates.size} selected template(s).
            </p>
            <input
              type="text"
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
              placeholder="tag1, tag2, tag3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 mb-4"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleBulkAddTags()
                }
              }}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkTagDialog(false)
                  setBulkTagInput('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={bulkActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddTags}
                disabled={bulkActionLoading || !bulkTagInput.trim()}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Adding Tags...' : 'Add Tags'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Change Dialog */}
      {showBulkCategoryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Change Category for Selected Templates</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a new category and optionally a subcategory for {selectedTemplates.size} template{selectedTemplates.size !== 1 ? 's' : ''}
            </p>

            {/* Category Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                value={selectedBulkCategory}
                onChange={(e) => {
                  setSelectedBulkCategory(e.target.value)
                  setSelectedBulkSubcategory('') // Reset subcategory when category changes
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a category...</option>
                {Array.isArray(categories) && categories.map((cat) => {
                  // Ensure cat is an object and has required properties
                  if (!cat || typeof cat !== 'object') {
                    console.warn('Invalid category object:', cat)
                    return null
                  }

                  const categoryId = String(cat.id || cat._id || '')
                  const categoryName = String(cat.name || 'Unnamed Category')

                  return (
                    <option key={categoryId} value={categoryId}>
                      {categoryName}
                    </option>
                  )
                })}
              </select>
            </div>

            {/* Subcategory Selection - Always show when category is selected */}
            {selectedBulkCategory && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory (Optional)</label>
                <select
                  value={selectedBulkSubcategory}
                  onChange={(e) => setSelectedBulkSubcategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={!selectedBulkCategory}
                >
                  <option value="">No subcategory</option>
                  {Array.isArray(subcategories) && subcategories.length > 0 ? (
                    subcategories.map((subcat) => {
                      // Ensure subcat is an object and has required properties
                      if (!subcat || typeof subcat !== 'object') {
                        console.warn('Invalid subcategory object:', subcat)
                        return null
                      }

                      const subcategoryId = String(subcat.id || subcat._id || '')
                      const subcategoryName = String(subcat.name || 'Unnamed Subcategory')

                      return (
                        <option key={subcategoryId} value={subcategoryId}>
                          {subcategoryName}
                        </option>
                      )
                    })
                  ) : (
                    <option value="" disabled>No subcategories available for this category</option>
                  )}
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkCategoryDialog(false)
                  setSelectedBulkCategory('')
                  setSelectedBulkSubcategory('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={bulkActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCategoryChange}
                disabled={bulkActionLoading || !selectedBulkCategory}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Updating...' : 'Update Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Scheduling Update Dialog */}
      {showBulkSchedulingDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Update Scheduling for Selected Templates</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Configure occasion scheduling for {selectedTemplates.size} template{selectedTemplates.size !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBulkSchedulingDialog(false)
                  setBulkSchedulingConfig({})
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <OccasionScheduler
                ref={occasionSchedulerRef}
                value={bulkSchedulingConfig}
                onChange={(config) => setBulkSchedulingConfig(config)}
                className="border-0"
              />
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkSchedulingDialog(false)
                  setBulkSchedulingConfig({})
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={bulkActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSchedulingUpdate}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {bulkActionLoading ? 'Updating...' : 'Apply Scheduling'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Name Change Dialog */}
      {showBulkNameChangeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[480px] max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Name Change</h3>
            <p className="text-sm text-gray-600 mb-4">
              Update name and/or description for {selectedTemplates.size} selected template{selectedTemplates.size !== 1 ? 's' : ''}.
              Leave a field empty to keep it unchanged.
            </p>
            
            {/* Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={bulkNameInput}
                onChange={(e) => setBulkNameInput(e.target.value)}
                placeholder="Enter new name for selected templates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Description Input */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <button
                  onClick={async () => {
                    if (!bulkNameInput.trim()) {
                      setMessageDialog({ isOpen: true, type: 'warning', title: 'Warning', message: 'Please enter a name first to generate title and description.' })
                      return
                    }
                    setBulkActionLoading(true)
                    try {
                      const resp = await templateAPI.generateDescription(bulkNameInput.trim())
                      if (resp.data.success || resp.data.status === 'success') {
                        const { title, description } = resp.data.data || {}
                        if (title) setBulkNameInput(title)
                        if (description) setBulkDescriptionInput(description)
                      } else {
                        setMessageDialog({ isOpen: true, type: 'error', title: 'Error', message: resp.data.message || 'Failed to generate' })
                      }
                    } catch (err) {
                      console.error('Error generating:', err)
                      setMessageDialog({ isOpen: true, type: 'error', title: 'Error', message: err.response?.data?.detail || 'Failed to generate from AI' })
                    } finally {
                      setBulkActionLoading(false)
                    }
                  }}
                  disabled={bulkActionLoading || !bulkNameInput.trim()}
                  className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  title="Generate enhanced title and description using AI"
                >
                  {bulkActionLoading ? (
                    <>
                      <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>Generate with AI</span>
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={bulkDescriptionInput}
                onChange={(e) => setBulkDescriptionInput(e.target.value)}
                placeholder="Enter new description for selected templates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-y min-h-[80px]"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkNameChangeDialog(false)
                  setBulkNameInput('')
                  setBulkDescriptionInput('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={bulkActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkNameChange}
                disabled={bulkActionLoading || (!bulkNameInput.trim() && !bulkDescriptionInput.trim())}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Dialog */}
      <MessageDialog
        isOpen={messageDialog.isOpen}
        type={messageDialog.type}
        title={messageDialog.title}
        message={messageDialog.message}
        onClose={() => setMessageDialog({ isOpen: false, type: 'info', title: '', message: '' })}
      />
    </div>
  )
}

// Numbered pagination control (1, 2, 3 ... n) with prev/next and ellipsis.
const Pagination = ({ page, totalPages, onPageChange, pageNumbers = [], disabled = false }) => {
  if (!totalPages || totalPages <= 1) return null

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {/* Previous */}
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={disabled || page <= 1}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Page numbers */}
      {pageNumbers.map((p, idx) =>
        p === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-400 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            disabled={disabled}
            className={`min-w-[2.25rem] px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:cursor-not-allowed ${
              p === page
                ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      {/* Next */}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={disabled || page >= totalPages}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  )
}

export default TemplateGrid
